import React, { useRef, useState } from "react";
import { Alert, Box, Button, IconButton, Paper, Typography, useTheme } from "@mui/material";

import FileCopyIcon from '@mui/icons-material/FileCopy';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

import { DataLayout, FieldControl, FileUpload, StatusChip } from "../../../shared/components";
import { formatDate } from "../../../shared/utils/formatDate";

import { useHandlePreSign } from "../../../hooks/useHandlePreSign";
import { useEditArtefacts } from "../mutation";
import { FileUploadRef } from "../../../shared/components/FileUpload";
import { ArtefactDetails as ArtefactDetailsType } from "../types";

export const ArtefactUploadTab = ({ artefactId, artefactDetails, refetch, refetchArtefacts }: {
    artefactId: string | null;
    artefactDetails: ArtefactDetailsType | undefined;
    refetch: () => void;
    refetchArtefacts: () => void;
}) => {
    const theme = useTheme();

    const editArtefacts = useEditArtefacts();
    const { handleDownload } = useHandlePreSign({});

    const fileUploadRef = useRef<FileUploadRef | null>(null);
    const [expiryDate, setExpiryDate] = useState<null | Date>(null);
    const [isSavingVersion, setIsSavingVersion] = useState(false);

    const onApproved = async (payload: { status: string, expiry_date?: Date | null, file_id?: string[] }) => {
        if (artefactId) {
            await editArtefacts.mutateAsync({ payload: payload, artefactId });
            refetch();
            refetchArtefacts();
            setExpiryDate(null);
        }
    };

    const onUpdateVersion = async () => {
        if (!fileUploadRef.current || !artefactId) return;

        setIsSavingVersion(true);
        fileUploadRef.current.setId(artefactId);
        await new Promise<void>((resolve) => {
            setTimeout(async () => {
                try {
                    await fileUploadRef.current?.uploadAllFiles();
                } finally {
                    resolve();
                }
            }, 0);
        });
        setIsSavingVersion(false);
    };

    const isCurrent = (file_id: string) => {
        const latestFile = artefactDetails?.files?.reduce((prev, current) => {
            return (new Date(prev.uploaded_date) > new Date(current.uploaded_date)) ? prev : current
        });
        return latestFile?.file_id === Number(file_id);
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
            <Box display={'flex'} flex={1} flexDirection={'column'} gap={1} sx={{
                overflowY: 'auto',
                pr: 1,
                '&::-webkit-scrollbar': {
                    width: '6px',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(0,0,0,0.2)',
                    borderRadius: '3px',
                },
            }}>
                {
                    artefactDetails?.files?.length === 0 ?
                        <Alert severity="info">
                            No File has been uploaded yet
                        </Alert> :
                        artefactDetails?.files?.map((value) => (
                            <Paper
                                data-background-elevation={1}
                                variant="outlined"
                                sx={{ p: 2, position: "relative", ...(isCurrent(String(value.file_id)) && { borderLeft: `2px solid ${theme.palette.success.main}` }) }}
                                key={value.file_id}
                            >
                                <Box display={'flex'} alignItems={'center'} gap={2}>
                                    <Box display={'flex'} alignItems={'center'} gap={1} flexDirection={"column"}>
                                        {
                                            isCurrent(String(value.file_id)) && (
                                                <StatusChip label="Current" color="green" variant="filled" sx={{ position: "absolute", top: 2, right: 2, borderRadius: 1, p: 0 }} />
                                            )
                                        }
                                        <FileCopyIcon sx={{ fontSize: 30, marginBottom: 1 }} />
                                        <Typography variant="caption" color="text.secondary" textAlign={"center"}>
                                            Version {value.version_id}
                                        </Typography>
                                    </Box>
                                    <Box display={'flex'} alignItems={'start'} flexDirection={'column'} gap={.5} flex={1} minWidth={0}>
                                        <Box display={'flex'} alignItems={'center'} marginBottom={.5} width={'100%'}>
                                            <Typography variant="subtitle2" color="text.primary" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                                {value.file_name}
                                            </Typography>
                                        </Box>
                                        <Box display={'flex'} alignItems={'start'} flexDirection={"column"}>
                                            <Typography variant="caption" color="text.secondary">
                                                Uploaded on {formatDate(value.uploaded_date)} by {value.assigned_to}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {value.expiry_date ? `Expires in ${formatDate(value.expiry_date)}` : '6:00PM'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <IconButton sx={{ ml: 'auto', flexShrink: 0 }} onClick={() => {
                                        const data = {
                                            type: value.file_type,
                                            name: value.file_name,
                                            s3_path: value.s3_path,
                                        }
                                        handleDownload(data)
                                    }}>
                                        <FileDownloadOutlinedIcon />
                                    </IconButton>
                                </Box>
                            </Paper>
                        ))
                }
            </Box>
            <Box display={'flex'} flexShrink={0} flexDirection={'column'} gap={2}>
                <DataLayout title="Upload New Version" bgVariant="filled">
                    <Box p={2}>
                        <FileUpload
                            type={"artefact"}
                            ref={fileUploadRef}
                            onUploadComplete={(file_id: string[]) => onApproved({ "status": "requiring_review", "expiry_date": expiryDate, file_id: file_id })}
                            typeId={artefactDetails?.id}
                            isHideFiles={true}
                            isAllowMultiple={false}
                            filesAllowed={['pdf', 'docx', 'csv', 'xlsx', 'zip']}
                            isRequired={true}
                        />
                    </Box>
                </DataLayout>
                <FieldControl
                    keyName={'expiryDate'}
                    label={"Expiry Date"}
                    variant={"datetime"}
                    value={expiryDate}
                    onChange={(_key: string, value: Date) => {
                        setExpiryDate(value)
                    }}
                />
                <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'} gap={2}>
                    <Button variant="outlined" onClick={() => setExpiryDate(null)} disabled={isSavingVersion || editArtefacts.isPending}>Clear</Button>
                    <Button
                        variant="contained"
                        disabled={!expiryDate || isSavingVersion || editArtefacts.isPending}
                        onClick={() => {
                            onUpdateVersion();
                        }}
                        loading={isSavingVersion || editArtefacts.isPending}
                    >
                        Save changes
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};
